/**
 * VideoWatermarkWrap — wraps any video (iframe / <video>) with a diagonal
 * watermark tile stamped with the current user's email + timestamp.
 *
 * Because YouTube iframes are cross-origin, we can't render inside them —
 * but we CAN stack an SVG watermark ABOVE them at low opacity. A single
 * corner badge stays fully visible + `pointer-events-none` so playback
 * controls still work. Any screen-recording captures the badge because
 * the recording tool sees the composed browser output, not the iframe
 * document.
 */
import React from 'react';

const VideoWatermarkWrap = ({ children, watermark, className = '', testId = 'video-watermark-wrap' }) => {
  const label = watermark?.email
    ? `${watermark.email} · ${new Date(watermark.issued_at || Date.now()).toLocaleString()}`
    : '';

  return (
    <div className={`relative ${className}`} data-testid={testId}>
      {children}

      {watermark?.email && (
        <>
          {/* Corner badge — always visible at ~35% opacity, high-contrast */}
          <div
            aria-hidden
            className="pointer-events-none absolute top-2 right-2 z-20 rounded-md px-2 py-1 text-[10px] font-bold text-white/80"
            style={{
              background: 'rgba(0,0,0,0.35)',
              backdropFilter: 'blur(4px)',
              textShadow: '0 1px 2px rgba(0,0,0,0.6)',
              letterSpacing: '0.02em',
              maxWidth: '70%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            data-testid="video-watermark-badge"
          >
            {label}
          </div>

          {/* Diagonal tiled watermark — semi-transparent overlay across the
              whole video surface. `pointer-events: none` so it never blocks
              play/pause/scrub. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-10 overflow-hidden"
            style={{ opacity: 0.14, mixBlendMode: 'overlay' }}
          >
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern
                  id={`fda-vwm-${watermark?.user_id || 'anon'}`}
                  width="420"
                  height="180"
                  patternUnits="userSpaceOnUse"
                  patternTransform="rotate(-24)"
                >
                  <text
                    x="0"
                    y="30"
                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                    fontSize="13"
                    fontWeight="700"
                    fill="#ffffff"
                    style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.55)', strokeWidth: 0.6 }}
                  >
                    {label}
                  </text>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill={`url(#fda-vwm-${watermark?.user_id || 'anon'})`} />
            </svg>
          </div>
        </>
      )}
    </div>
  );
};

export default VideoWatermarkWrap;
